/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */
/* eslint-disable no-param-reassign */
import { types, applySnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';
import * as constants from '../../../../common/constants';
import { normalizeTelemetryDetailResponse } from '../utils/telemetryUtils';

const INITIAL_STATE = {
  data: {},
  tracingData: {},
  selectedRecordIndex: 0,
  isOverlayOpen: false,
  isLoading: false
};

const TelemetryDetailModel = types
  .model('TelemetryDetailModel', {
    data: types.frozen(),
    tracingData: types.optional(types.frozen(), {}),
    selectedRecordIndex: types.optional(types.number, 0),
    isOverlayOpen: types.optional(types.boolean, false),
    isLoading: types.optional(types.boolean, false)
  })
  .actions((self) => {
    const eventManager = Framework.getEventManager();

    return {
      fetch(payload) {
        self.isLoading = true;
        eventManager.publish(constants.GET_TELEMETRY_DETAIL, { payload });
      },
      prepareForFetch(index) {
        applySnapshot(self, {
          data: {},
          tracingData: {},
          selectedRecordIndex: index,
          isOverlayOpen: true,
          isLoading: true
        });
      },
      setResponse(response) {
        const normalized =
          response?.displayData !== undefined
            ? { displayData: response.displayData, tracingData: response.tracingData }
            : normalizeTelemetryDetailResponse(response);

        applySnapshot(self, {
          data: normalized.displayData || {},
          tracingData: normalized.tracingData || {},
          selectedRecordIndex: self.selectedRecordIndex,
          isOverlayOpen: self.isOverlayOpen,
          isLoading: false
        });
        eventManager.publish(constants.GET_TELEMETRY_DETAIL_RESPONSE);
      },
      setSelectedRecordIndex(index) {
        applySnapshot(self, { ...self, selectedRecordIndex: index });
      },
      setIsOverlayOpen(value) {
        applySnapshot(self, { ...self, isOverlayOpen: value });
      },
      resetStateValues() {
        applySnapshot(self, INITIAL_STATE);
        eventManager.publish(constants.GET_TELEMETRY_DETAIL_RESPONSE);
      },
      resetStoreValues() {
        applySnapshot(self, { ...INITIAL_STATE, data: {}, tracingData: {} });
        eventManager.publish(constants.GET_TELEMETRY_DETAIL_RESPONSE);
      }
    };
  })
  .views((self) => ({
    get currentSelectedRecordIndex() {
      return self.selectedRecordIndex;
    },
    get currentOverlayOpenedState() {
      return self.isOverlayOpen;
    },
    get detailData() {
      return self.data;
    },
    get tracingPayload() {
      return self.tracingData;
    }
  }));

export default TelemetryDetailModel;
